import { Form, FormInstance, notification, Steps, Typography } from "antd";
import { useEffect, useState } from "react";
import { UploadFile } from "antd/lib/upload/interface";
import JobInfoPage from "./JobInfoPage";
import AttachFilesPage from "./AttachFilesPage";
import {
  CoverWizardForm,
  FlowType,
  FormGroupTouched,
  StateCode,
} from "../../CoverWizardModal";
import moment, { Moment } from "moment";
import MaterialSpacingColorPage from "./MaterialSpacingColorPage";
import { getUserFromStorage } from "../../../../../../util/localStorage.util";
import {
  getCustomerAddresses,
  getCustomerInfo,
} from "../../../../../../store/actions/customerActionCreators";
import { getStateList } from "../../../../../../store/actions/adminActionCreators";
import AdditionalHardwarePage from "./AdditionalHardwarePage";
import MeasurementsPage from "./MeasurementsPage";
import ImportantConsiderationsPage from "./ImportantConsiderationsPage";
import CornersPage from "./CornersPage";
import SpaCoverPage from "./SpaCoverPage";
import EdgesPage from "./EdgesPage";
import DeckingPage from "./DeckingPage";
import ShippingInformationPage, {
  AutofilledValue,
  AutofillRadio,
  useShippingInformationPageState,
} from "./ShippingInformationPage";
import AutocoverPage from "./AutocoverPage";
import CustomerInfoDto from "../../../../../../store/actions/dtos/CustomerInfoDto";
import { CustomerAddressDto } from "../../../../../../store/actions/dtos/CustomerAddressDto";
import { getHardware } from "../../../../../../store/actions/hardwareCreators";
import useGetFeatureFlagStatuses from "../../../../../../hooks/useGetFeatureFlagStatuses";
import OrderTypePage from "./OrderTypePage";
import SelectOrderPage from "./SelectOrderPage";
import Flex from "../../../../../../components/Flex";

const AllFormPages = ({
  startWithOrderTypePage,
  formIsSubmitting,
  wizardPageNumberState: [wizardPageNumber, setWizardPageNumber],
  formGroupTouchedState: [formGroupTouched, setFormGroupTouched],
  quoteIsRGA,
  hideReturnTypes,
}: {
  startWithOrderTypePage?: boolean;
  wizardPageNumberState: [number, React.Dispatch<React.SetStateAction<number>>];
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
  formIsSubmitting: boolean;
  quoteIsRGA?: boolean;
  hideReturnTypes?: boolean;
}) => {
  const [usStateOptions, setUsStateOptions] = useState<StateCode[]>([]);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoDto | null>(
    null
  );
  const [customerAddresses, setCustomerAddresses] = useState<
    CustomerAddressDto[] | null
  >(null);
  const [otherHardware, setOtherHardware] = useState<string[] | null>(null);
  const [extraLongWallAnchors, setExtraLongWallAnchors] = useState<
    string[] | null
  >(null);

  //State moved here to ensure the page can resume from where it left off
  const shippingPageState = useShippingInformationPageState();

  const form = Form.useFormInstance<CoverWizardForm>();

  useEffect(() => {
    fetchStateList();
  }, []);

  async function fetchStateList() {
    let [response, err]: Array<any> = await getStateList();
    if (response) {
      if (response.status === 200) {
        setUsStateOptions(response.data.data);
      }
    }
    if (err) {
      setUsStateOptions([]);
    }
  }

  function showNotification(message: any) {
    notification.open({
      message: message,
    });
  }

  async function _getCustomerInfo() {
    if (!customerInfo) {
      let [response, err] = await getCustomerInfo(0);
      if (response?.status === 200) {
        setCustomerInfo(response.data.data);
        return response.data.data;
      }
      if (err) {
        showNotification("Failed to get customer info");
      }
    } else {
      return customerInfo;
    }
  }

  async function _getCustomerAddresses() {
    if (!customerAddresses) {
      let [response, err] = await getCustomerAddresses();
      if (response?.statusCode === 200) {
        setCustomerAddresses(response.data);
        return response.data;
      }
      if (err) {
        showNotification("Failed to get customer addresses");
      }
    } else {
      return customerAddresses;
    }
  }

  async function _getHardware() {
    let [response, err] = await getHardware();
    if (response?.statusCode === 200) {
      const _otherHardware: string[] = [];
      const _extraLongWallAnchors: string[] = [];
      response.data.forEach((item) => {
        if (item.includes("EXTRA LONG WALL ANCHOR")) {
          _extraLongWallAnchors.push(item);
        } else {
          _otherHardware.push(item);
        }
      });
      setOtherHardware(_otherHardware);
      setExtraLongWallAnchors(_extraLongWallAnchors);
      return response.data;
    }
    if (err) {
      showNotification("Failed to get hardware");
    }
  }

  const { advancedFlow } = useGetFeatureFlagStatuses();

  const pagesWithoutOrderType = advancedFlow ? 12 : 3;
  const totalPages = startWithOrderTypePage
    ? pagesWithoutOrderType + 1
    : pagesWithoutOrderType;
  const currentPageIdx = startWithOrderTypePage
    ? wizardPageNumber - 1
    : wizardPageNumber - 2;

  return (
    <>
      {!advancedFlow && (
        <PageNumber pageNumber={currentPageIdx} totalPages={totalPages} />
      )}
      {wizardPageNumber === 1 ? (
        <SelectOrderPage
          wizardPageNumberState={[wizardPageNumber, setWizardPageNumber]}
          onNextButtonClick={() => setWizardPageNumber(2)}
          hideBackButton
        />
      ) : wizardPageNumber === 2 ? (
        <JobInfoPage
          hideBackButton={!startWithOrderTypePage}
          onNextButtonClick={() => setWizardPageNumber(3)}
          onBackButtonClick={
            startWithOrderTypePage ? () => setWizardPageNumber(1) : undefined
          }
          usStateOptions={usStateOptions}
          quoteIsRGA={quoteIsRGA}
          hideReturnTypes={hideReturnTypes}
        />
      ) : wizardPageNumber === 3 ? (
        <ShippingInformationPage
          onNextButtonClick={() => setWizardPageNumber(4)}
          onBackButtonClick={() => setWizardPageNumber(2)}
          usStateOptions={usStateOptions}
          getCustomerInfo={_getCustomerInfo}
          getCustomerAddresses={_getCustomerAddresses}
          shippingInformationPageState={shippingPageState}
        />
      ) : wizardPageNumber === 4 ? (
        <AttachFilesPage
          wizardPageNumberState={[wizardPageNumber, setWizardPageNumber]}
          onNextButtonClick={() => setWizardPageNumber(5)}
          onBackButtonClick={() => setWizardPageNumber(3)}
          advancedFlowEnabled={advancedFlow}
          formIsSubmitting={formIsSubmitting}
        />
      ) : wizardPageNumber === 5 ? (
        <MeasurementsPage
          wizardPageNumberState={[wizardPageNumber, setWizardPageNumber]}
          onNextButtonClick={() => setWizardPageNumber(6)}
          onBackButtonClick={() => setWizardPageNumber(4)}
        />
      ) : wizardPageNumber === 6 ? (
        <MaterialSpacingColorPage
          formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
          initialFormValues={form.getFieldsValue(true)}
          onNextButtonClick={() => setWizardPageNumber(7)}
          onBackButtonClick={() => setWizardPageNumber(5)}
        />
      ) : wizardPageNumber === 7 ? (
        <AdditionalHardwarePage
          wizardPageNumberState={[wizardPageNumber, setWizardPageNumber]}
          onNextButtonClick={() => setWizardPageNumber(8)}
          onBackButtonClick={() => setWizardPageNumber(6)}
          getHardware={_getHardware}
          extraLongWallAnchors={extraLongWallAnchors}
          otherHardware={otherHardware}
        />
      ) : wizardPageNumber === 8 ? (
        <ImportantConsiderationsPage
          formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
          onNextButtonClick={() => setWizardPageNumber(9)}
          onBackButtonClick={() => setWizardPageNumber(7)}
          initialFormValues={form.getFieldsValue(true)}
        />
      ) : wizardPageNumber === 9 ? (
        <CornersPage
          formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
          onNextButtonClick={() => setWizardPageNumber(10)}
          onBackButtonClick={() => setWizardPageNumber(8)}
          initialFormValues={form.getFieldsValue(true)}
        />
      ) : wizardPageNumber === 10 ? (
        <SpaCoverPage
          initialFormValues={form.getFieldsValue(true)}
          onNextButtonClick={() => setWizardPageNumber(11)}
          onBackButtonClick={() => setWizardPageNumber(9)}
          formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
        />
      ) : wizardPageNumber === 11 ? (
        <EdgesPage
          formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
          initialFormValues={form.getFieldsValue(true)}
          onNextButtonClick={() => setWizardPageNumber(12)}
          onBackButtonClick={() => setWizardPageNumber(10)}
        />
      ) : wizardPageNumber === 12 ? (
        <DeckingPage
          formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
          initialFormValues={form.getFieldsValue(true)}
          onNextButtonClick={() => setWizardPageNumber(13)}
          onBackButtonClick={() => setWizardPageNumber(11)}
        />
      ) : wizardPageNumber === 13 ? (
        <AutocoverPage
          formIsSubmitting={formIsSubmitting}
          formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
          initialFormValues={form.getFieldsValue(true)}
          onBackButtonClick={() => setWizardPageNumber(12)}
        />
      ) : null}
    </>
  );
};

function PageNumber({
  pageNumber,
  totalPages,
}: {
  pageNumber: number;
  totalPages: number;
}) {
  const { Step } = Steps;
  return (
    <Flex justify="center" style={{ width: "100%", margin: "0 0 20px 0" }}>
      <Steps
        responsive={false}
        style={{ width: "100%", maxWidth: "300px" }}
        current={pageNumber}
      >
        {Array.from({ length: totalPages }).map((_, index) => (
          <Step key={index} />
        ))}
      </Steps>
    </Flex>
  );
}

export default AllFormPages;
